/**
 * HomePage - Olympic Fit Wellness
 * Luxury Design System - Range Rover Inspired
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ChevronRight,
  MapPin,
  Play,
  Check,
  Star,
  Dumbbell,
  Apple,
  Heart,
  Brain,
  Users,
  Calendar,
  Smartphone,
  Trophy,
  Quote
} from 'lucide-react';
import api from '../lib/api';

export default function HomePage() {
  const [locations, setLocations] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [activeProgram, setActiveProgram] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [locationsData, tiersData] = await Promise.all([
        api.getLocations(),
        api.getMembershipTiers()
      ]);
      setLocations(locationsData?.slice(0, 4) || []);
      setTiers(tiersData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const services = [
    {
      icon: Dumbbell,
      title: 'Fitness Training',
      description: 'Personal training, group classes, and virtual workouts designed to help you reach your fitness goals with expert guidance.',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600',
      link: '/services#fitness'
    },
    {
      icon: Apple,
      title: 'Nutrition Counseling',
      description: 'Custom meal plans, nutrition coaching, and metabolic testing to fuel your body for optimal performance and health.',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600',
      link: '/services#nutrition'
    },
    {
      icon: Heart,
      title: 'Recovery & Spa',
      description: 'Advanced recovery services including sauna, hydromassage, cryotherapy, and massage therapy for complete restoration.',
      image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600',
      link: '/services#recovery'
    },
    {
      icon: Brain,
      title: 'Mental Wellness',
      description: 'Mindfulness programs, meditation classes, stress management, and mental health support for complete wellness.',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600',
      link: '/services#wellness'
    }
  ];

  const programs = [
    {
      title: 'Personal Training',
      description: '1-on-1 customized training programs with certified experts who create personalized plans based on your goals.',
      features: ['Customized workout plans', 'Nutrition guidance', 'Progress tracking', 'Flexible scheduling'],
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800'
    },
    {
      title: 'Group Classes',
      description: '500+ weekly classes including yoga, HIIT, cycling, strength training, boxing, and more.',
      features: ['All fitness levels welcome', 'Expert instructors', 'Community atmosphere', 'Diverse class types'],
      image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800'
    },
    {
      title: 'Virtual Wellness',
      description: 'Access 1,000+ on-demand workouts and live-streamed classes from anywhere, anytime.',
      features: ['On-demand library', 'Live classes', 'Mobile app access', 'Progress sync'],
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800'
    },
    {
      title: 'Corporate Programs',
      description: 'Custom wellness solutions for businesses to improve employee health and productivity.',
      features: ['Team challenges', 'Wellness seminars', 'On-site training', 'Health analytics'],
      image: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=800'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      result: 'Lost 50 lbs',
      quote: 'Olympic Fit Wellness changed my life. The comprehensive approach to fitness and nutrition helped me lose 50 pounds and keep it off.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300'
    },
    {
      name: 'Michael T.',
      result: 'Marathon Ready',
      quote: 'From couch to marathon in 8 months. The trainers and community support made the impossible feel achievable.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300'
    },
    {
      name: 'Jennifer L.',
      result: 'Life Transformed',
      quote: 'More than just fitness - the mental wellness programs helped me manage stress and find balance in my busy life.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300'
    }
  ];

  const shopItems = [
    { name: 'OFW Training Tee', price: 35, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400' },
    { name: 'Performance Hoodie', price: 75, image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400' },
    { name: 'Training Shorts', price: 45, image: 'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=400' },
    { name: 'OFW Water Bottle', price: 25, image: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400' }
  ];

  return (
    <div className="bg-white" data-testid="home-page">
      {/* Hero Section - Luxury Full Screen */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80"
            alt="Olympic Fit Wellness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-6 lg:px-12 pt-32 pb-20">
          <div className="max-w-3xl">
            <p className="text-sm font-heading tracking-[0.15em] text-white/80 mb-6">
              OLYMPIC FIT WELLNESS
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-heading font-normal text-white mb-6 tracking-[0.04em] leading-tight uppercase" data-testid="hero-title">
              Elevate Your<br/>Wellness Journey
            </h1>
            <p className="text-base md:text-lg text-white/70 mb-10 max-w-lg tracking-wide leading-relaxed">
              Transform your wellness journey with world-class facilities, personalized training programs, and a community dedicated to excellence.
            </p>
            <Link 
              to="/membership" 
              className="inline-flex items-center gap-3 bg-white text-black font-heading font-medium px-10 py-4 text-base tracking-[0.08em] hover:bg-neutral-100 transition-all duration-300"
              data-testid="cta-explore"
            >
              EXPLORE MEMBERSHIP
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm border-t border-white/10">
          <div className="container mx-auto px-6 lg:px-12 py-8">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-heading font-normal text-white tracking-wider">15+</div>
                <div className="text-xs md:text-sm text-white/50 tracking-[0.1em] mt-1">PREMIUM LOCATIONS</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-normal text-white tracking-wider">50K+</div>
                <div className="text-xs md:text-sm text-white/50 tracking-[0.1em] mt-1">ACTIVE MEMBERS</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-normal text-white tracking-wider">500+</div>
                <div className="text-xs md:text-sm text-white/50 tracking-[0.1em] mt-1">WEEKLY CLASSES</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Wellness Section */}
      <section className="py-24 lg:py-32" data-testid="services-section">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">OUR SERVICES</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase">
              Comprehensive Wellness
            </h2>
            <p className="text-neutral-500 max-w-xl mx-auto mt-6 text-base tracking-wide">
              More than fitness. Complete wellness. We address every aspect of your health journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-1">
            {services.map((service, idx) => (
              <Link 
                key={idx}
                to={service.link}
                className="group relative overflow-hidden"
                data-testid={`service-card-${idx}`}
              >
                <div className="aspect-[3/4] relative">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="text-xl font-heading font-normal text-white mb-3 tracking-[0.06em] uppercase">
                      {service.title}
                    </h3>
                    <p className="text-white/60 text-base mb-6 line-clamp-2 tracking-wide">
                      {service.description}
                    </p>
                    <span className="text-sm font-heading tracking-[0.1em] text-white inline-flex items-center gap-2 group-hover:gap-4 transition-all duration-300">
                      DISCOVER <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Founders Section - With Flag Image */}
      <section className="py-24 lg:py-32 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Founders Image - Flag Photo */}
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/qgz7yuub_image.png"
                alt="Rae Edwards and Clint Fuqua - Founders of Olympic Fit Wellness"
                className="w-full h-auto object-cover"
              />
            </div>
            
            {/* Founders Content */}
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-6 block">THE FOUNDERS</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase mb-8">
                United by Excellence
              </h2>
              <p className="text-neutral-600 mb-6 tracking-wide leading-relaxed text-base">
                Olympic Fit Wellness was founded by two visionaries united by a passion for transforming lives through wellness. Monzavous "Rae" Edwards, a world-class sprint athlete, and Clint Fuqua, "The Health Engineer," combined their unique expertise to create a wellness experience unlike any other.
              </p>
              <p className="text-neutral-600 mb-10 tracking-wide leading-relaxed text-base">
                Their philosophy is simple: bring Olympic-level training methodology and systematic health engineering to everyone, regardless of their fitness background.
              </p>
              
              <div className="flex gap-12 mb-10">
                <div>
                  <div className="text-3xl font-heading font-normal text-neutral-900 tracking-wider">10.00s</div>
                  <div className="text-sm text-neutral-500 tracking-[0.08em] mt-1">RAE'S 100M BEST</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-normal text-neutral-900 tracking-wider">25+</div>
                  <div className="text-sm text-neutral-500 tracking-[0.08em] mt-1">YEARS COMBINED</div>
                </div>
              </div>

              <Link 
                to="/about/founders" 
                className="inline-flex items-center gap-3 text-sm font-heading tracking-[0.1em] text-neutral-900 hover:text-neutral-600 transition-colors"
              >
                MEET THE FOUNDERS <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Programs Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">YOUR JOURNEY</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase">
              Signature Programs
            </h2>
          </div>

          {/* Program Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-16">
            {programs.map((program, idx) => (
              <button
                key={idx}
                onClick={() => setActiveProgram(idx)}
                className={`px-8 py-4 text-sm font-heading tracking-[0.08em] transition-all duration-300 ${
                  activeProgram === idx 
                    ? 'bg-black text-white' 
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {program.title.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Active Program Content */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl md:text-3xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase mb-6">
                {programs[activeProgram].title}
              </h3>
              <p className="text-neutral-600 mb-8 tracking-wide leading-relaxed text-base">
                {programs[activeProgram].description}
              </p>
              <ul className="space-y-4 mb-10">
                {programs[activeProgram].features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-4 text-neutral-700 text-base tracking-wide">
                    <Check className="w-5 h-5 text-neutral-900" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link 
                to="/services" 
                className="inline-flex items-center gap-3 bg-black text-white font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-all duration-300"
              >
                LEARN MORE <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={programs[activeProgram].image}
                  alt={programs[activeProgram].title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFW Mobile App Section */}
      <section className="py-24 lg:py-32 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-6 block">TRAIN ANYWHERE</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-white tracking-[0.04em] uppercase mb-8">
                OFW Mobile App
              </h2>
              <p className="text-neutral-400 mb-10 tracking-wide leading-relaxed text-base">
                Your wellness journey continues beyond our facilities. Access on-demand workouts, book classes, track nutrition, and stay connected with your OFW community.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                {[
                  { icon: Calendar, text: 'Book Classes Instantly' },
                  { icon: Dumbbell, text: '1,000+ On-Demand Workouts' },
                  { icon: Apple, text: 'Nutrition Tracking' },
                  { icon: Users, text: 'Community Challenges' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-12 h-12 border border-neutral-700 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white text-base tracking-wide">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <a href="#" className="inline-block">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" className="h-12" />
                </a>
                <a href="#" className="inline-block">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-12" />
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600"
                  alt="OFW Mobile App"
                  className="max-w-xs shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="py-24 lg:py-32" data-testid="membership-section">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">MEMBERSHIP</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase mb-6">
              Choose Your Path
            </h2>
            <p className="text-neutral-500 max-w-xl mx-auto text-base tracking-wide">
              Flexible membership options designed to fit your lifestyle and goals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-1 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <div 
                key={tier.tier}
                className={`relative p-10 ${
                  tier.highlight 
                    ? 'bg-neutral-900 text-white' 
                    : 'bg-white border border-neutral-200'
                }`}
                data-testid={`membership-tier-${tier.tier}`}
              >
                {tier.highlight && (
                  <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-white text-black text-xs font-heading px-6 py-2 tracking-[0.1em]">
                    POPULAR
                  </div>
                )}
                
                <h3 className={`text-xl font-heading font-normal tracking-[0.06em] uppercase mb-4 ${tier.highlight ? 'text-white' : 'text-neutral-900'}`}>
                  {tier.name}
                </h3>
                
                <div className="flex items-baseline gap-1 mb-8">
                  <span className={`text-5xl font-heading font-normal tracking-wider ${tier.highlight ? 'text-white' : 'text-neutral-900'}`}>
                    ${tier.price_monthly}
                  </span>
                  <span className={`text-base tracking-wide ${tier.highlight ? 'text-neutral-400' : 'text-neutral-500'}`}>/mo</span>
                </div>
                
                <ul className="space-y-4 mb-10">
                  {tier.features?.slice(0, 5).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.highlight ? 'text-white' : 'text-neutral-900'}`} />
                      <span className={`tracking-wide ${tier.highlight ? 'text-neutral-300' : 'text-neutral-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Link 
                  to={`/membership?tier=${tier.tier}`}
                  className={`block w-full text-center py-4 font-heading text-sm tracking-[0.08em] transition-all duration-300 ${
                    tier.highlight 
                      ? 'bg-white text-black hover:bg-neutral-100' 
                      : 'bg-black text-white hover:bg-neutral-800'
                  }`}
                >
                  SELECT PLAN
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-24 lg:py-32 bg-neutral-50" data-testid="locations-section">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">OUR LOCATIONS</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase">
                15+ Premium Locations
              </h2>
            </div>
            <Link to="/locations" className="inline-flex items-center gap-3 text-sm font-heading tracking-[0.08em] text-neutral-900 hover:text-neutral-600 transition-colors">
              VIEW ALL LOCATIONS <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-1">
            {locations.map((location) => (
              <Link 
                key={location.location_id}
                to={`/locations/${location.location_id}`}
                className="group"
                data-testid={`location-${location.location_id}`}
              >
                <div className="aspect-[4/3] overflow-hidden mb-6">
                  <img 
                    src={location.image_url || 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600'}
                    alt={location.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase group-hover:text-neutral-600 transition-colors">
                  {location.name}
                </h3>
                <p className="text-base text-neutral-500 flex items-center gap-2 mt-2 tracking-wide">
                  <MapPin className="w-4 h-4" />
                  {location.city}, {location.state}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">MEMBER SUCCESS</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase">
              Success Stories
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-1 max-w-5xl mx-auto">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white border border-neutral-200 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 object-cover"
                  />
                  <div>
                    <h4 className="text-lg font-heading font-normal text-neutral-900 tracking-wide">{testimonial.name}</h4>
                    <p className="text-sm text-neutral-500 tracking-wider uppercase">{testimonial.result}</p>
                  </div>
                </div>
                <p className="text-neutral-600 text-base italic tracking-wide leading-relaxed">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link 
              to="/success-stories" 
              className="inline-flex items-center gap-3 bg-black text-white font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-all duration-300"
            >
              VIEW ALL STORIES <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* OFW Collection Section */}
      <section className="py-24 lg:py-32 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">SHOP</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase">
                OFW Collection
              </h2>
            </div>
            <Link to="/shop" className="inline-flex items-center gap-3 text-sm font-heading tracking-[0.08em] text-neutral-900 hover:text-neutral-600 transition-colors">
              SHOP COLLECTION <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-1">
            {shopItems.map((item, idx) => (
              <Link key={idx} to="/shop" className="group">
                <div className="aspect-square overflow-hidden mb-6 bg-white">
                  <img 
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-heading font-normal text-neutral-900 tracking-wide group-hover:text-neutral-600 transition-colors">
                  {item.name}
                </h3>
                <p className="text-neutral-500 text-base tracking-wide mt-1">${item.price}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 lg:py-32 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-6 block">OUR PHILOSOPHY</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-white tracking-[0.04em] uppercase mb-8 max-w-3xl mx-auto leading-relaxed">
            More Than Fitness.<br/>Complete Wellness.
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-12 tracking-wide leading-relaxed text-base">
            At Olympic Fit Wellness, we believe true health encompasses physical fitness, nutrition, recovery, mental wellness, and community. Our comprehensive approach addresses every aspect of your wellness journey.
          </p>
          <Link 
            to="/about" 
            className="inline-flex items-center gap-3 bg-white text-black font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-100 transition-all duration-300"
          >
            LEARN MORE <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-6 block">STAY CONNECTED</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase mb-6">
              Join the OFW Community
            </h2>
            <p className="text-neutral-500 mb-10 tracking-wide text-base">
              Subscribe to our newsletter for wellness tips, exclusive offers, and community updates.
            </p>
            <form className="flex flex-col sm:flex-row gap-2 max-w-lg mx-auto" onSubmit={(e) => e.preventDefault()}>
              <input 
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 border border-neutral-300 text-base tracking-wide focus:outline-none focus:border-black transition-colors"
                data-testid="newsletter-email"
              />
              <button 
                type="submit" 
                className="bg-black text-white font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-all duration-300"
                data-testid="newsletter-submit"
              >
                SUBSCRIBE
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
